import { colors as arcadeColors } from '@arcade/web'
import * as tokens from '@cash-design-system/tokens'

import taxesColors from './taxesColors'

// please do not use from this palette directly, instead prefer the semantic colors below
export const palette = {
  black: tokens.ColorPaletteBlack,
  gray20: tokens.ColorPaletteGray20,
  gray40: tokens.ColorPaletteGray40,
  gray60: tokens.ColorPaletteGray60,
  gray75: tokens.ColorPaletteGray75,
  gray85: tokens.ColorPaletteGray85,
  gray90: tokens.ColorPaletteGray90,
  gray96: tokens.ColorPaletteGray96,
  gray98: tokens.ColorPaletteGray98,
  white: tokens.ColorPaletteWhite,
  cobalt: tokens.ColorPaletteCobalt,
  cobalt5: tokens.ColorPaletteCobalt5,
  cobalt10: tokens.ColorPaletteCobalt10,
  cobalt20: tokens.ColorPaletteCobalt20,
  cobalt30: tokens.ColorPaletteCobalt30,
  cobalt40: tokens.ColorPaletteCobalt40,
  cobalt60: tokens.ColorPaletteCobalt60,
  behindCobalt: tokens.ColorPaletteBehindCobalt,
  bgGreen: tokens.ColorPaletteBgGreen,
  lightModeGreen: tokens.ColorPaletteLightModeGreen,
  darkModeGreen: tokens.ColorPaletteDarkModeGreen,
  lightModeBannerGreen: '#F9FFFB',
  darkModeBannerGreen: '#25452C',
  disabledGreen: '#80eaa7',
  tooltipGreen: '#4CD964',
  activeGreen: '#00a83e',
  onGreen: tokens.ColorPaletteOnGreen,
  lightModeAmber: tokens.ColorPaletteAmber,
  darkModeAmber: '#E0490C',
  ocean: tokens.ColorPaletteOcean,
  pink: tokens.ColorPalettePink,
  purple: tokens.ColorPalettePurple,
  red: tokens.ColorPaletteRed,
  darkRed: '#D7040E',
  royal: tokens.ColorPaletteRoyal,
  scarlet: tokens.ColorPaletteScarlet,
  sky: tokens.ColorPaletteSky,
  steel: tokens.ColorPaletteSteel,
  sunshine: tokens.ColorPaletteSunshine,
  turquoise: tokens.ColorPaletteTurquoise,
  bitcoinBlue: tokens.ColorPaletteBitcoinBlue,
  bgBitcoinBlue: tokens.ColorPaletteBgBitcoinBlue,
  investingPurpleLight: tokens.ColorPaletteInvestingPurpleLight,
} as const

// These are the semantic colors
const colors = {
  primary: {
    light: palette.lightModeGreen,
    dark: palette.darkModeGreen,
  },
  tint: {
    light: tokens.ColorTintGreenLight,
    dark: tokens.ColorTintGreenDark,
  },
  green: {
    light: tokens.ColorTintGreenLight,
    dark: tokens.ColorTintGreenDark,
  },
  onGreen: {
    light: palette.onGreen,
    dark: palette.onGreen,
  },
  onGreenBackground: {
    light: palette.lightModeBannerGreen,
    dark: palette.darkModeBannerGreen,
  },
  success: {
    light: 'green',
    dark: 'green',
    comment: 'A more semantic alias for the "semantic" green.',
  },
  active: {
    light: palette.activeGreen,
    dark: palette.activeGreen,
  },
  disabled: {
    light: palette.disabledGreen,
    dark: palette.disabledGreen,
  },
  tooltip: {
    light: palette.tooltipGreen,
    dark: palette.tooltipGreen,
  },
  verificationTint: {
    light: tokens.ColorTintVerificationLight,
    dark: tokens.ColorTintVerificationDark,
    comment: 'The tint color for verification flows.',
  },
  error: {
    light: palette.red,
    dark: palette.red,
  },
  errorHover: {
    light: '#DF3A42',
    dark: '#DF3A42',
  },
  errorPressed: {
    light: palette.darkRed,
    dark: palette.darkRed,
  },
  warning: {
    light: palette.lightModeAmber,
    dark: palette.darkModeAmber,
  },
  behindBackground: {
    light: tokens.ColorBackgroundBehindLight,
    dark: tokens.ColorBackgroundBehindDark,
    comment: 'The color for content behind the main background.',
  },
  cardTabBackground: {
    light: tokens.ColorBackgroundCardTabLight,
    dark: tokens.ColorBackgroundCardTabDark,
  },
  background: {
    light: tokens.ColorBackgroundMainLight,
    dark: tokens.ColorBackgroundMainDark,
    comment: 'The color for the main background of an interface.',
  },
  secondaryBackground: {
    light: tokens.ColorBackgroundSecondaryLight,
    dark: tokens.ColorBackgroundSecondaryDark,
    comment: 'The color for content layered on top of the main background.',
  },
  tertiaryBackground: {
    light: tokens.ColorBackgroundTertiaryLight,
    dark: tokens.ColorBackgroundTertiaryDark,
    comment: 'The color for content layered on top of secondary backgrounds.',
  },
  placeholderBackground: {
    light: tokens.ColorBackgroundPlaceholderLight,
    dark: tokens.ColorBackgroundPlaceholderDark,
    comment: 'The color for the background of a view while content is loading (e.g. avatars).',
  },
  elevatedBackground: {
    light: tokens.ColorBackgroundElevatedLight,
    dark: tokens.ColorBackgroundElevatedDark,
    comment: 'The color for content presented modally over existing content.',
  },
  secondaryElevatedBackground: {
    light: tokens.ColorBackgroundElevatedSecondaryLight,
    dark: tokens.ColorBackgroundElevatedSecondaryDark,
    comment: 'The color for content layered on top of modally presented content.',
  },
  transparentImageBackground: {
    light: palette.gray96,
    dark: 'secondaryBackground',
    comment: 'The background color for transparent images.',
  },
  behindCardBackground: {
    light: palette.gray96,
    dark: palette.cobalt,
    comment: 'The color for the main background of the web interface.',
  },
  cardBackground: {
    light: palette.white,
    dark: palette.cobalt5,
    comment: 'The color for the background of content cards of the web interface.',
  },
  label: {
    light: tokens.ColorLabelDefaultLight,
    dark: tokens.ColorLabelDefaultDark,
    comment: 'The color for text that contains primary content.',
  },
  secondaryLabel: {
    light: tokens.ColorLabelSecondaryLight,
    dark: tokens.ColorLabelSecondaryDark,
    comment: 'The color for text that contains secondary content.',
  },
  tertiaryLabel: {
    light: tokens.ColorLabelTertiaryLight,
    dark: tokens.ColorLabelTertiaryDark,
    comment: 'The color for text that contains tertiary content.',
  },
  placeholderLabel: {
    light: tokens.ColorLabelPlaceholderLight,
    dark: tokens.ColorLabelPlaceholderDark,
    comment: 'The color for placeholder text in text fields.',
  },
  disabledLabel: {
    light: tokens.ColorLabelDisabledLight,
    dark: tokens.ColorLabelDisabledDark,
    comment: 'The color for text that is disabled.',
  },
  marketingLabel: {
    light: palette.black,
    dark: palette.white,
    comment: 'Text color used for general copy and headers in a marketing context.',
  },
  cursor: {
    light: 'primary',
    dark: 'primary',
    comment: 'The color for the input cursor in a text field.',
  },
  clearButtonTint: {
    light: tokens.ColorInputClearButtonTintLight,
    dark: tokens.ColorInputClearButtonTintDark,
    comment: 'The background color of the clear button in a text field.',
  },
  primaryButtonBackground: {
    light: '#00D64F',
    dark: '#00BD46',
    comment: 'The background color of a Mooncake primary button.',
  },
  primaryButtonHover: {
    light: '#00C147',
    dark: '#009738',
    comment: 'The background color of a Mooncake primary button when being hovered over.',
  },
  primaryButtonActive: {
    light: '#00A93E',
    dark: '#008431',
    comment: 'The background color of a Mooncake primary button when being clicked.',
  },
  primaryButtonTint: {
    light: tokens.ColorButtonPrimaryTintLight,
    dark: tokens.ColorButtonPrimaryTintDark,
    comment: 'The foreground color of a Mooncake primary button.',
  },
  secondaryButtonBackground: {
    light: '#E8E8E8',
    dark: '#3B3C3E',
    comment: 'The background color of a Mooncake secondary button.',
  },
  secondaryButtonHover: {
    light: '#DCDCDC',
    dark: '#353638',
    comment: 'The background color of a Mooncake secondary button when being hovered over.',
  },
  secondaryButtonActive: {
    light: '#CECECE',
    dark: '#2F3032',
    comment: 'The background color of a Mooncake secondary button when being clicked.',
  },
  secondaryButtonTint: {
    light: tokens.ColorButtonSecondaryTintLight,
    dark: tokens.ColorButtonSecondaryTintDark,
    comment: 'The foreground color of a Mooncake secondary button.',
  },
  tertiaryButtonTint: {
    light: 'primaryButtonBackground',
    dark: 'primaryButtonBackground',
    comment: 'The foreground color of a Mooncake tertiary button.',
  },
  outlineButtonSelectedBorder: {
    light: tokens.ColorButtonOutlineSelectedBorderLight,
    dark: tokens.ColorButtonOutlineSelectedBorderDark,
    comment: 'The selected state border color of a Mooncake outline button.',
  },
  formFieldBackground: {
    light: tokens.ColorPaletteGray96,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The background color of form fields.',
  },
  formFieldBackgroundDisabled: {
    light: tokens.ColorPaletteGray98,
    dark: tokens.ColorPaletteCobalt10,
    comment: 'The background color of form fields when disabled.',
  },
  segmentedControlForeground: {
    light: tokens.ColorPaletteWhite,
    dark: tokens.ColorPaletteCobalt60,
    comment: 'The foreground color of a large segmented control.',
  },
  segmentedControlBackground: {
    light: tokens.ColorPaletteGray90,
    dark: tokens.ColorPaletteCobalt30,
    comment: 'The background color of a large segmented control.',
  },
  pageControlSelected: {
    light: tokens.ColorButtonPageControlSelectedLight,
    dark: tokens.ColorButtonPageControlSelectedDark,
    comment: 'The foreground color of the selected indicator dot in a page control.',
  },
  pageControlUnselected: {
    light: tokens.ColorButtonPageControlUnselectedLight,
    dark: tokens.ColorButtonPageControlUnselectedDark,
    comment: 'The foreground color of the unselected indicator dots in a page control.',
  },
  icon: {
    light: tokens.ColorIconDefaultLight,
    dark: tokens.ColorIconDefaultDark,
  },
  secondaryIcon: {
    light: tokens.ColorIconSecondaryLight,
    dark: tokens.ColorIconSecondaryDark,
  },
  tertiaryIcon: {
    light: tokens.ColorIconTertiaryLight,
    dark: tokens.ColorIconTertiaryDark,
  },
  placeholderIcon: {
    light: tokens.ColorIconPlaceholderLight,
    dark: tokens.ColorIconPlaceholderDark,
  },
  disabledIcon: {
    light: tokens.ColorIconDisabledLight,
    dark: tokens.ColorIconDisabledDark,
  },
  chevron: {
    light: tokens.ColorIconChevronLight,
    dark: tokens.ColorIconChevronDark,
    comment: 'The color used for chevron arrow icons.',
  },
  dragHandle: {
    light: tokens.ColorIconDragHandleLight,
    dark: tokens.ColorIconDragHandleDark,
    comment: 'The color used for drag handles on sheets.',
  },
  hairline: {
    light: tokens.ColorPaletteGray90,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The color used for separators that should blend in with their background.',
  },
  outline: {
    light: tokens.ColorOutlineLight,
    dark: tokens.ColorOutlineDark,
    comment: 'The color used for outlined content and separators that should be more prominent than their background.',
  },
  unselectedPasscodeDot: {
    light: tokens.ColorUnselectedPasscodeDotLight,
    dark: tokens.ColorUnselectedPasscodeDotDark,
  },
  widgetForeground: {
    light: tokens.ColorWidgetForegroundLight,
    dark: tokens.ColorWidgetForegroundDark,
    comment: 'The color used for foreground content of the Today View widget.',
  },
  keyboard: {
    light: tokens.ColorKeyboardLight,
    dark: tokens.ColorKeyboardDark,
    comment: 'The color used for the foreground of a custom numeric keyboard.',
  },
  cardCustomizationStroke: {
    light: tokens.ColorCardCustomizationStrokeLight,
    dark: tokens.ColorCardCustomizationStrokeDark,
  },
  paymentPadBackground: {
    light: tokens.ColorPaymentPadBackgroundLight,
    dark: tokens.ColorPaymentPadBackgroundDark,
    comment: 'The color used for the background of the main payment pad.',
  },
  paymentPadButtonBackground: {
    light: tokens.ColorPaymentPadButtonBackgroundLight,
    dark: tokens.ColorPaymentPadButtonBackgroundDark,
    comment: 'The color used for buttons on the main payment pad.',
  },
  paymentPadGhostedTextColor: {
    light: tokens.ColorPaymentPadGhostedTextLight,
    dark: tokens.ColorPaymentPadGhostedTextDark,
    comment: 'The color used for ghosted characters on the main payment pad.',
  },
  paymentPadKeyboard: {
    light: tokens.ColorPaymentPadKeyboardLight,
    dark: tokens.ColorPaymentPadKeyboardDark,
    comment: 'The color used for the foreground of the custom numeric keyboard on the main payment pad.',
  },
  bitcoinBlue: {
    light: palette.bitcoinBlue,
    dark: palette.bitcoinBlue,
  },
  bitcoinPaymentPadBackground: {
    light: tokens.ColorPaymentPadBitcoinBackgroundLight,
    dark: tokens.ColorPaymentPadBitcoinBackgroundDark,
    comment: 'The color used for the background of the bitcoin main payment pad.',
  },
  bitcoinPaymentPadButtonBackground: {
    light: tokens.ColorPaymentPadBitcoinButtonBackgroundLight,
    dark: tokens.ColorPaymentPadBitcoinButtonBackgroundDark,
    comment: 'The color used for buttons on the bitcoin main payment pad.',
  },
  scrollHint: {
    light: tokens.ColorActivityScrollHintLight,
    dark: tokens.ColorActivityScrollHintDark,
  },
  investing: {
    light: tokens.ColorInvestingPurpleLight,
    dark: tokens.ColorInvestingPurpleDark,
  },
  bannerTextSuccess: {
    light: '#018206',
    dark: '#00D64F',
  },
  bannerBackgroundSuccess: {
    light: palette.lightModeBannerGreen,
    dark: palette.darkModeBannerGreen,
  },
  bannerButtonSuccess: {
    light: '#D7F8E3',
    dark: '#2C5633',
  },
  bannerButtonSuccessPressed: {
    light: '#C7EDD9',
    dark: '#306134',
  },
  bannerHairlineSuccess: {
    light: '#D7F8E3',
    dark: '#2C5633',
  },
  bannerTextError: {
    light: '#D7040E',
    dark: '#FF545C',
  },
  bannerBackgroundError: {
    light: '#FFF8F8',
    dark: '#3A2323',
  },
  bannerButtonError: {
    light: '#D7F8E3',
    dark: '#2C5633',
  },
  bannerButtonErrorPressed: {
    light: '#C7EDD9',
    dark: '#306134',
  },
  bannerHairlineError: {
    light: '#D7F8E3',
    dark: '#2C5633',
  },
  bannerTextWarn: {
    light: '#A06503',
    dark: '#CBA624',
  },
  bannerBackgroundWarn: {
    light: '#FFFCE5',
    dark: '#292500',
  },
  errorPageBackground: {
    light: palette.white,
    dark: palette.cobalt,
  },
  refundAmount: {
    light: 'primary',
    dark: 'primary',
    comment: 'The font color for the refund amount',
  },
  taxesReviewAndFileBannerBackground: {
    light: 'primaryBannerBackground',
    dark: 'bannerBackgroundSuccess',
  },
  taxesReviewAndFileBannerText: {
    light: palette.white,
    dark: 'bannerTextSuccess',
  },
  taxesReviewAndFileBannerButtonTint: {
    light: 'primaryBannerBackground',
    dark: 'bannerBackgroundSuccess',
  },
  taxesReviewAndFileBannerButtonBackground: {
    light: palette.white,
    dark: palette.white,
  },
  taxesLoginMobileColor: {
    light: palette.gray96,
    dark: palette.cobalt5,
    comment: 'The color for text in the mobile login screen',
  },
  primaryBannerText: {
    light: 'primaryButtonTint',
    dark: 'primaryButtonTint',
  },
  primaryBannerBackground: {
    light: palette.bgGreen,
    dark: 'bannerBackgroundSuccess',
  },
  link: {
    light: 'primaryButtonBackground',
    dark: 'primaryButtonBackground',
    comment: 'The color for link text.',
  },
  textButtonLabelHover: {
    light: 'label',
    dark: 'tertiaryLabel',
    comment: 'The color for text that contains secondary content when being hovered over.',
  },
  body: {
    light: palette.gray40,
    dark: palette.cobalt60,
    comment: 'The color for general body text.',
  },
  progressBarFill: {
    light: 'primary',
    dark: 'primary',
    comment: 'progress bar fill color',
  },
  progressBarBackground: {
    light: tokens.ColorPaletteGray96,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'progress bar background color',
  },
  searchBackground: {
    light: palette.gray96,
    dark: palette.cobalt5,
    comment: 'Review and File inactive progress bar background color.',
  },
  errorLink: {
    light: palette.scarlet,
    dark: palette.scarlet,
    comment: 'The color for error link text.',
  },
  errorLinkHover: {
    light: '#FC6A6A',
    dark: '#FC6A6A',
    comment: 'The hover color for error link text.',
  },
  waitingTint: {
    light: palette.lightModeGreen,
    dark: palette.darkModeGreen,
    comment: 'Color used for waiting tint.',
  }, // TODO: update links to use arcade tokens (bianca/asif)
  blueLink: {
    light: '#0000ff',
    dark: '#8EB5FF',
    comment: 'The color for blue link text.',
  },
  blueLinkVisited: {
    light: '#660199',
    dark: '#AC8EFF',
    comment: 'The color for blue link text that has been visited.',
  },
  navigationItemActiveBackground: {
    light: 'bannerButtonSuccess',
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The color for the background of navigation items when being clicked.',
  },
  navigationItemActiveText: {
    light: palette.activeGreen,
    dark: palette.white,
    comment: 'The color for the text of navigation items when being clicked.',
  },
  nestedCardOutline: {
    light: tokens.ColorPaletteGray96,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The color for the outline between nested cards.',
  },
  loginSpinner: {
    light: palette.white,
    dark: palette.darkModeGreen,
    comment: 'The loading spinner color for login',
  },
  loginBackground: {
    light: tokens.ColorTintGreenLight,
    dark: tokens.ColorBackgroundMainDark,
    comment: 'The background color for login.',
  },
  clickableContentBackgroundHover: {
    light: tokens.ColorPaletteGray96,
    dark: tokens.ColorPaletteCobalt10,
    comment:
      'The color for the background of clickable content like nested cards or rows within a card when being hovered over.',
  },
  nestedCardOutlineHover: {
    light: tokens.ColorPaletteGray90,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The color for the outline between nested cards when being hovered over.',
  },
  nestedCardOutlineActive: {
    light: tokens.ColorPaletteGray85,
    dark: tokens.ColorPaletteCobalt30,
    comment: 'The color for the outline between nested cards when being clicked.',
  },
  rowBackgroundActive: {
    light: tokens.ColorPaletteGray90,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The color for the background of content rows when being clicked.',
  },
  receiptFooterBackground: {
    light: tokens.ColorPaletteGray96,
    dark: tokens.ColorPaletteCobalt10,
    comment: 'The color for the background of receipt footers.',
  },
  inputOutline: {
    light: tokens.ColorPaletteGray85,
    dark: tokens.ColorPaletteCobalt40,
    comment: 'The color for the outline of checkboxes and radio buttons.',
  },
  withinGrayCardInputOutline: {
    light: '#949494',
    dark: tokens.ColorPaletteCobalt40,
    comment: 'The color for the outline of radio buttons when nested within a gray card.',
  },
  tertiaryNavigationItemBackgroundActive: {
    light: tokens.ColorPaletteGray96,
    dark: tokens.ColorPaletteCobalt20,
    comment: 'The color for the background of tertiary navigation items when being clicked or selected.',
  },
  tertiaryNavigationItemBackgroundHover: {
    light: tokens.ColorPaletteGray98,
    dark: tokens.ColorPaletteCobalt10,
    comment: 'The color for the background of tertiary navigation items when being hovered over.',
  },
  mutedIcon: {
    light: palette.white,
    dark: palette.cobalt,
    comment:
      "The color of the split payment Icon's border and the trident itself being used in checkout in lending locations.",
  },
  checkboxSelected: {
    light: 'primary',
    dark: 'primary',
    comment: 'The color for selected checkboxes',
  },
  taxesLogoIcon: {
    light: 'primary',
    dark: 'primary',
    comment: 'The color for the taxes logo icon',
  },
  taxesLogoInnerIcon: {
    light: palette.white,
    dark: palette.white,
    comment: 'The color for the inner part of the taxes logo icon',
  },
  inputFocusBorder: {
    light: 'primary',
    dark: 'primary',
    comment: 'The color for the focus border of inputs',
  },
  headerIconBackground: {
    light: 'primary',
    dark: 'primary',
    comment: 'The color for large icons that are typically at the top of the page',
  },
  red: {
    light: tokens.ColorTintGreenLight,
    dark: tokens.ColorTintGreenDark,
  },
  iconSubtle: {
    light: arcadeColors.icon.subtle.light,
    dark: arcadeColors.icon.subtle.dark,
  },
} as const

export const taxesColorsTheme = {
  ...colors,
  ...taxesColors,
} as ThemeColorsOverride

export type PaletteColor = keyof typeof palette
export type Colors = keyof typeof colors
export type ThemeColors = { [key in Colors]: typeof colors[key]['light'] | typeof colors[key]['dark'] }

// TODO string type should be palette type
export type ThemeColorsOverride = { [key in Colors]: { light: string; dark: string } }

export const themeColorExists = (color: Colors) => colors[color] != null
export const getPaletteColorName = (color: string) => {
  return Object.keys(palette).find((name: string) => {
    const paletteName = name as PaletteColor
    return palette[paletteName] === color
  })
}
export const getThemeColor = (
  themeColors: Partial<ThemeColorsOverride>,
  isDark: boolean,
  key: Colors,
  stack: string[] = []
): string => {
  if (stack.includes(key)) {
    /* eslint-disable-next-line */
    console.warn(`circular color reference [isDark:${isDark}]: [${[...stack, key].join('->')}]`)
    return key
  }

  const semanticColor = themeColors[key]

  const color = (isDark ? semanticColor?.dark : semanticColor?.light) as Colors
  if (themeColorExists(color)) {
    return getThemeColor(themeColors, isDark, color, [...stack, key])
  }
  return color
}

export const getThemeColors = ({
  themeColors,
  isDark,
}: {
  themeColors: Partial<ThemeColorsOverride>
  isDark: boolean
}): ThemeColors => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Object.keys(themeColors).reduce((set: any, key: string) => {
    const color = key as Colors
    set[color] = getThemeColor(themeColors, isDark, color)
    return set
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {} as any)
}
export type Theme = { colors: ThemeColors; darkMode: boolean }

type ThemeArgs = { theme: Theme } | Theme
export type ThemeVariant = 'default' | 'taxes'

export const getColor = (color: Colors) => (args: ThemeArgs) => {
  if ('colors' in args) {
    return args.colors[color]
  } else {
    return args.theme.colors[color]
  }
}

export const getColorsByThemeVariant = ({
  themeVariant,
  darkMode,
}: {
  themeVariant: ThemeVariant
  darkMode: boolean
}) => {
  let themeColors: ThemeColorsOverride

  switch (themeVariant) {
    case 'taxes':
      themeColors = taxesColorsTheme
      break
    case 'default':
      themeColors = colors
      break
    default:
      themeColors = colors
      break
  }
  return getThemeColors({ themeColors, isDark: darkMode })
}

export default colors
